/**
 * StockStateField — the 4-value stock-state radio group (EPIC-006 Phase 7,
 * Task 7.2). Mirrors the frozen CHECK enum exactly
 * (`in_stock | low | out_of_stock | unknown`). Labels come from the
 * `seller-dashboard` i18n namespace (`stock.*`).
 *
 * A semantic `<fieldset>`/radio group (not a custom segmented control) keeps
 * it natively keyboard-operable and screen-reader announced; each option is a
 * ≥44px target.
 */
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import {
  registerSellerDashboardI18n,
  SELLER_DASHBOARD_NS,
  STOCK_STATES,
  type StockState,
} from "@/features/seller-dashboard/api";

export interface StockStateFieldProps {
  value: StockState;
  onChange: (value: StockState) => void;
}

export function StockStateField({
  value,
  onChange,
}: StockStateFieldProps): ReactElement {
  registerSellerDashboardI18n();
  const { t } = useTranslation(SELLER_DASHBOARD_NS);
  return (
    <fieldset data-testid="stock-state-field" className="flex flex-wrap gap-2">
      <legend className="mb-2 text-sm font-semibold text-foreground">
        {t("editor.sections.stock")}
      </legend>
      {STOCK_STATES.map((s) => {
        const selected = value === s;
        return (
          <label
            key={s}
            className={[
              "inline-flex min-h-[44px] cursor-pointer items-center gap-2 rounded-[var(--g-radius)]",
              "border px-3 text-sm transition-colors",
              selected
                ? "border-primary bg-secondary text-secondary-foreground"
                : "border-border bg-card text-foreground",
            ].join(" ")}
          >
            <input
              type="radio"
              name="stock_state"
              value={s}
              checked={selected}
              onChange={() => onChange(s)}
              className="accent-[var(--g-primary)]"
            />
            {t(`stock.${s}`)}
          </label>
        );
      })}
    </fieldset>
  );
}

export default StockStateField;
